import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ApiService } from '../../../services/api.service';
import { Subscription } from 'rxjs';
import { PermissionsService } from '../../../services/permission.service';
import { DialogConfig, FormField } from '../../../shared/components/reusable-dialog/form-field.interface';

@Component({
  selector: 'app-vendor-master',
  standalone: false,
  templateUrl: './vendor-master.component.html',
  styleUrl: './vendor-master.component.scss'
})
export class VendorMasterComponent implements OnInit, OnDestroy {
  title = 'Incubator Table';
  showDialog: boolean = false;
  isUpdatedialog: boolean = false;
  
  // Reusable dialog configuration
  dialogConfig: DialogConfig = {
    title: 'Vendor Details',
    width: '80vw',
    maximizable: true,
    closable: true,
    dismissableMask: true,
    formLayout: 'two-column',
    submitButtonText: 'Submit',
    cancelButtonText: 'Cancel',
    showCancelButton: true,
    fields: [
      {
        key: 'vendor',
        label: 'Vendor Name',
        type: 'text',
        placeholder: 'Enter vendor name',
        validation: { required: true },
        maxLength: 100
      },
      {
        key: 'contactPerson',
        label: 'Contact Person\'s Name',
        type: 'text',
        placeholder: 'Enter contact person name',
        validation: { 
          required: true, 
          pattern: '^[A-Za-z\\s]+$' 
        },
        patternErrorMessage: 'Only alphabets are allowed',
        maxLength: 100
      },
      {
        key: 'email',
        label: 'Email',
        type: 'email',
        placeholder: 'Enter email address',
        validation: { required: true }
      },
      {
        key: 'phone',
        label: 'Phone Number',
        type: 'text',
        placeholder: 'Enter 10-digit number',
        validation: { 
          required: true,
          pattern: '^[0-9]{10}$'
        },
        patternErrorMessage: 'Enter a valid 10-digit phone number',
        maxLength: 10
      },
      {
        key: 'address',
        label: 'Address',
        type: 'textarea',
        placeholder: 'Enter address',
        validation: { 
          required: true,
          pattern: '^[a-zA-Z0-9 ,.()&-]+$'
        },
        maxLength: 500
      },
      {
        key: 'taxNumber',
        label: 'Tax Registration Number',
        type: 'text',
        placeholder: 'Enter tax registration number',
        validation: { 
          required: true,
          pattern: '^[a-zA-Z0-9 ,.()&-]+$'
        },
        maxLength: 100
      },
      {
        key: 'bankDetails',
        label: 'Bank Account Details',
        type: 'textarea',
        placeholder: 'Enter bank account details',
        validation: { required: true },
        maxLength: 500
      }
    ]
  };

  system: any[];
  cols: any[] = [
    { field: 'name', header: 'Name', filterType: 'text' },
    { field: 'contact', header: 'Contact', filterType: 'text' },
    { field: 'email', header: 'Email', filterType: 'text' },
  ];
  
  selectedsystem: any;
  systemSection: any;
  showDeleteDialog: boolean = false;
  venderList: any[]=[];
  private subscriptions = new Subscription();
  
  // For reusable dialog
  initialData: any = {};
  viewMode: boolean = false;
  selectedvendor: any;

  constructor(private fb: FormBuilder, private api: ApiService, private messageService: MessageService, private permissionService: PermissionsService) { }

  ngOnInit(): void {
    this.getSystem();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  totalRecords: number = 0;

  getSystem(event: { page: number; search?: string } = { page: 1 }) {
    const page = event.page || 1;
    const search = event.search?.trim();

    let url = `/master/vendor/?page=${page}`;
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    const sub = this.api.getAPI(url).subscribe((res) => {
      this.venderList = res.data || res.results;
      this.totalRecords = res.count || 0;
    });
    this.subscriptions.add(sub);
  }

  addSystem() {
    this.initialData = {};
    this.viewMode = false;
    this.selectedvendor = null;
    this.showDialog = true;
  }

  onSubmit(formData: any) {
    const payload: any = {
      name: formData.vendor,
      email: formData.email,
      contact: formData.contactPerson,
      phone_number: formData.phone,
      address: formData.address,
      tax_number: formData.taxNumber,
      bank_details: formData.bankDetails,
      status: 1
    };

    if (this.selectedvendor) {
      const sub = this.api.putAPI(`/master/vendor/${this.selectedvendor.id}/`, payload).subscribe({
        next: (res: any) => {
          this.getSystem();
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Vendor updated successfully'
          });
          this.showDialog = false;
          this.initialData = {};
          this.selectedvendor = null;
        },
        error: (err) => {
          console.error('Error while saving mapping:', err);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to save mapping. Please try again.'
          });
        }
      });
      this.subscriptions.add(sub);
    } else {
      const sub = this.api.postAPI('/master/vendor/', payload).subscribe({
        next: (res: any) => {
          this.getSystem();
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Vendor added successfully'
          });
          this.showDialog = false;
          this.initialData = {};
        },
        error: (err) => {
          console.error('Error while saving mapping:', err);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to save mapping. Please try again.'
          });
        }
      });
      this.subscriptions.add(sub);
    }
  }

  viewSystem(rowData: any) {
    this.viewMode = true;
    if (rowData && rowData.id) {
      this.selectedsystem = rowData;
      this.isUpdatedialog = true;
      this.initialData = {
        vendor: rowData.name,
        contactPerson: rowData.contact,
        email: rowData.email,
        phone: rowData.phone_number,
        address: rowData.address,
        taxNumber: rowData.tax_number,
        bankDetails: rowData.bank_details
      };
      this.showDialog = true;
    }
  }

  onEdit(event: any) {
    this.viewMode = false;
    this.showDialog = true;
    this.selectedvendor = event;
    this.initialData = {
      vendor: this.selectedvendor.name,
      contactPerson: this.selectedvendor.contact,
      email: this.selectedvendor.email,
      phone: this.selectedvendor.phone_number,
      address: this.selectedvendor.address,
      taxNumber: this.selectedvendor.tax_number,
      bankDetails: this.selectedvendor.bank_details
    };
  }

  showDeletePopup(equipment: any) {
    this.showDeleteDialog = true;
    this.selectedsystem = equipment;
  }

  deleteSystem() {
    let sectionId = this.selectedsystem.id;
    const sub = this.api.deleteAPI(`/master/vendor/${sectionId}/`).subscribe({
      next: (res: any) => {
        this.showDeleteDialog = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Vendor Deleted Successfully',
        });
        this.getSystem();
      },
      error: (err) => {
        console.error('API Error:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to delete the proposal. Please try again.',
        });
      },
    });
    this.subscriptions.add(sub);
  }

  allowOnlyNumbers(event: KeyboardEvent) {
    const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight'];
    if (allowedKeys.includes(event.key)) return;

    const isNumber = /^[0-9]$/.test(event.key);
    if (!isNumber) {
      event.preventDefault();
    }
  }

  // Reusable dialog event handlers
  onDialogSubmit(formData: any): void {
    this.onSubmit(formData);
  }

  onDialogCancel(): void {
    this.showDialog = false;
    this.initialData = {};
    this.selectedvendor = null;
  }

  onDialogClose(): void {
    this.showDialog = false;
    this.initialData = {};
    this.selectedvendor = null;
  }

  get canEditSetup(): boolean {
    return this.permissionService.hasPrivilege('/master/vendor-master', 'Edit') ||
           this.permissionService.hasPrivilege('/master/vendor-master', 'Delete') ||
           this.permissionService.hasPrivilege('/master/vendor-master', 'View');
  }
}